import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldAlert, Clock } from "lucide-react";

// Idle-lock: after IDLE_MS of no activity we show a warning modal that
// requires the user to re-enter their password. If they don't respond
// within WARNING_MS we auto-logout. Applies to ALL signed-in users.
const IDLE_MS = 2 * 60 * 60 * 1000;   // 2h
const WARNING_MS = 5 * 60 * 1000;     // last 5 minutes
const CHECK_INTERVAL = 15 * 1000;     // recheck every 15s
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];

export default function InactivityGate() {
  const { user, reauth, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [remaining, setRemaining] = useState(WARNING_MS);
  const lastActivity = useRef(Date.now());

  const markActive = useCallback(() => {
    // Don't reset the timer while the modal is up — that would defeat the
    // whole point of the challenge.
    if (!showWarning) lastActivity.current = Date.now();
  }, [showWarning]);

  const forceLogout = useCallback(async () => {
    setShowWarning(false);
    setPassword("");
    try { await logout(); } catch { /* ignore */ }
    toast.error("Signed out — inactive for 2 hours.");
  }, [logout]);

  useEffect(() => {
    if (!user) return undefined;
    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, markActive, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, markActive));
    };
  }, [user, markActive]);

  useEffect(() => {
    if (!user) return undefined;
    lastActivity.current = Date.now();  // reset on new session
    const iv = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_MS) {
        forceLogout();
      } else if (idle >= IDLE_MS - WARNING_MS && !showWarning) {
        setShowWarning(true);
        setRemaining(IDLE_MS - idle);
      } else if (showWarning) {
        setRemaining(Math.max(0, IDLE_MS - idle));
      }
    }, CHECK_INTERVAL);
    return () => clearInterval(iv);
  }, [user, showWarning, forceLogout]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      await reauth(password);
      lastActivity.current = Date.now();
      setShowWarning(false);
      setPassword("");
      toast.success("Session extended.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Incorrect password");
    } finally { setBusy(false); }
  };

  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000).toString().padStart(2, "0");

  if (!user) return null;

  return (
    <Dialog open={showWarning} onOpenChange={(open) => { if (!open) forceLogout(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="inactivity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display font-extrabold">
            <ShieldAlert className="h-5 w-5 text-amber-600" /> You've been idle
          </DialogTitle>
          <DialogDescription>
            For security, sessions are ended after 2 hours of inactivity.
            Re-enter your password to keep working, or you'll be signed out.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-sm bg-amber-50 border border-amber-200 px-3 py-2 text-sm flex items-center gap-2 text-amber-900"
             data-testid="inactivity-countdown">
          <Clock className="h-4 w-4" />
          Automatic sign-out in
          <strong className="font-mono">{mins}:{secs}</strong>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2" data-testid="inactivity-form">
          <div className="space-y-1.5">
            <Label htmlFor="inactivity-pwd" className="label-overline">Password</Label>
            <Input id="inactivity-pwd" type="password" autoFocus required
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="rounded-sm h-11"
              data-testid="inactivity-password-input" />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || !password}
              className="flex-1 h-10 rounded-sm bg-[#002FA7] hover:bg-[#00227A]"
              data-testid="inactivity-continue-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue session"}
            </Button>
            <Button type="button" variant="outline" onClick={forceLogout}
              className="h-10 rounded-sm"
              data-testid="inactivity-signout-button">
              Sign out
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
